import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowLeft, Heart, MessageCircle, Flag, Ban, Trash2, Send, ShieldCheck, Lock, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api, track } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { COMMUNITY } from "@/constants/testIds";
import { MicButton } from "@/components/MicButton";

function Composer({ circle, onPosted }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const draftKey = `sh_community_draft_${circle.id}`;
  const [body, setBody] = useState(() => {
    try { return JSON.parse(localStorage.getItem(draftKey) || "{}").body || ""; } catch { return ""; }
  });
  const [anon, setAnon] = useState(() => {
    try { return !!JSON.parse(localStorage.getItem(draftKey) || "{}").anon; } catch { return false; }
  });
  const [image, setImage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showSubscribe, setShowSubscribe] = useState(false);
  const isMember = user?.is_premium || user?.role === "owner";

  // Autosave the unpublished draft so it's waiting after a trial member subscribes.
  useEffect(() => {
    try {
      if (body.trim() || anon) localStorage.setItem(draftKey, JSON.stringify({ body, anon }));
      else localStorage.removeItem(draftKey);
    } catch { /* noop */ }
  }, [body, anon, draftKey]);

  const pickImage = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader(); r.onload = () => setImage(r.result); r.readAsDataURL(f);
  };
  const submit = async () => {
    if (!body.trim() && !image) return;
    // Trial members can draft freely, but publishing is a paid feature — prompt to subscribe.
    if (!isMember) { setShowSubscribe(true); return; }
    setSaving(true);
    try {
      await api.post("/community/posts", { circle_id: circle.id, body, anonymous: anon, image });
      track("community_post_created", { circle: circle.id });
      setBody(""); setImage(null);
      try { localStorage.removeItem(draftKey); } catch { /* noop */ }
      onPosted();
    } finally { setSaving(false); }
  };
  return (
    <div className="bg-white border border-[#291B2D]/5 rounded-2xl p-5 card-soft">
      <p className="text-sm text-[#59535B] mb-3 italic">{circle.prompt}</p>
      <div className="relative">
        <textarea data-testid={COMMUNITY.composerInput} value={body} onChange={(e) => setBody(e.target.value)}
          placeholder="Share what's on your heart, or tap the mic to speak…"
          className="w-full min-h-[90px] bg-[#FCFAF6] border border-[#E5DED4] rounded-xl p-4 pr-14 focus:outline-none focus:ring-2 focus:ring-[#C89B55]" />
        <MicButton testid="community-composer-dictate" className="absolute bottom-3 right-3 h-9 w-9"
          onAppend={(t) => setBody((b) => (b ? b + " " + t : t))} />
      </div>
      {image && (
        <div className="relative mt-3 inline-block">
          <img src={image} alt="attachment preview" className="max-h-40 rounded-xl" />
          <button onClick={() => setImage(null)} aria-label="Remove image"
            className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-[#291B2D] text-white flex items-center justify-center text-xs">×</button>
        </div>
      )}
      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-3">
          <button data-testid={COMMUNITY.anonymousToggle} onClick={() => setAnon((a) => !a)}
            className="flex items-center gap-2 text-sm text-[#59535B]">
            <span className={`h-5 w-9 rounded-full flex items-center px-0.5 transition-all ${anon ? "bg-[#C89B55]" : "bg-[#D6CFC7]"}`}>
              <span className={`h-4 w-4 rounded-full bg-white transition-all ${anon ? "translate-x-4" : ""}`} />
            </span>
            Anonymous
          </button>
          <label data-testid="community-post-image" className="flex items-center gap-1.5 text-sm text-[#59535B] cursor-pointer hover:text-[#291B2D]">
            <ImageIcon className="h-4 w-4" /> Photo
            <input type="file" accept="image/*" onChange={pickImage} className="hidden" />
          </label>
        </div>
        <button data-testid={COMMUNITY.postButton} onClick={submit} disabled={saving || (!body.trim() && !image)}
          className="rounded-full bg-[#291B2D] text-[#FCFAF6] hover:bg-[#352238] px-6 py-2.5 font-medium disabled:opacity-40 transition-smooth">
          {saving ? "Posting…" : "Share"}
        </button>
      </div>

      {showSubscribe && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowSubscribe(false)} data-testid="community-subscribe-prompt">
          <div className="w-full max-w-sm bg-white rounded-3xl p-6 text-center card-soft" onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-[#FBF4E7] flex items-center justify-center">
              <Lock className="h-6 w-6 text-[#C89B55]" />
            </div>
            <h3 className="font-display text-xl font-semibold text-[#291B2D]">Ready to share with the sisterhood?</h3>
            <p className="text-sm text-[#59535B] mt-2">Posting is a member feature. Your draft is safe right here — subscribe to publish it. You can keep reading, reacting, and commenting during your trial.</p>
            <button data-testid="community-subscribe-cta" onClick={() => navigate("/paywall")}
              className="mt-5 w-full rounded-full bg-[#291B2D] text-[#FCFAF6] hover:bg-[#352238] px-6 py-3 font-semibold transition-smooth">
              Subscribe to publish
            </button>
            <button data-testid="community-subscribe-keep-editing" onClick={() => setShowSubscribe(false)}
              className="mt-2 w-full rounded-full px-6 py-2.5 text-sm text-[#59535B] hover:text-[#291B2D]">
              Keep editing my draft
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const REACTIONS = ["❤️", "🙌", "🥹", "🔥", "🤝"];

function CommentItem({ comment }) {
  const [reactions, setReactions] = useState(comment.reactions || {});
  const [myReactions, setMyReactions] = useState(comment.my_reactions || []);
  const toggleReaction = async (emoji) => {
    const { data } = await api.post(`/community/comments/${comment.comment_id}/react`, { emoji });
    setReactions((prev) => { const n = { ...prev }; n[emoji] = (n[emoji] || 0) + (data.reacted ? 1 : -1); if (n[emoji] <= 0) delete n[emoji]; return n; });
    setMyReactions((prev) => (data.reacted ? [...prev, emoji] : prev.filter((e) => e !== emoji)));
  };
  return (
    <div className="text-sm" data-testid={`community-comment-${comment.comment_id}`}>
      <span className="font-medium">{comment.author_name}: </span>
      <span className="text-[#252326]">{comment.body}</span>
      {comment.image && <img src={comment.image} alt="reply attachment" className="mt-2 rounded-lg max-h-56 w-auto" />}
      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
        {REACTIONS.map((e) => {
          const mine = myReactions.includes(e); const n = reactions[e] || 0;
          return (
            <button key={e} data-testid={`community-comment-react-${e}`} onClick={() => toggleReaction(e)}
              className={`text-xs rounded-full px-2 py-0.5 border transition-smooth ${mine ? "bg-[#F3EEF4] border-[#7B4C74]/40" : "bg-white border-[#291B2D]/10 hover:bg-[#F7F2EA]"}`}>
              <span>{e}</span>{n > 0 && <span className="ml-1 text-[#59535B]">{n}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PostCard({ post, onChange }) {
  const [liked, setLiked] = useState(post.liked);
  const [likes, setLikes] = useState(post.like_count);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [cbody, setCbody] = useState("");
  const [cimage, setCimage] = useState(null);
  const [reactions, setReactions] = useState(post.reactions || {});
  const [myReactions, setMyReactions] = useState(post.my_reactions || []);

  const like = async () => {
    const { data } = await api.post(`/community/posts/${post.post_id}/like`);
    setLiked(data.liked); setLikes((n) => n + (data.liked ? 1 : -1));
  };
  const loadComments = async () => {
    const { data } = await api.get(`/community/posts/${post.post_id}/comments`);
    setComments(data);
  };
  const toggleComments = () => { const n = !showComments; setShowComments(n); if (n) loadComments(); };
  const addComment = async () => {
    if (!cbody.trim() && !cimage) return;
    await api.post(`/community/posts/${post.post_id}/comments`, { body: cbody, image: cimage });
    setCbody(""); setCimage(null); loadComments();
  };
  const pickCommentImage = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader(); r.onload = () => setCimage(r.result); r.readAsDataURL(f);
  };
  const toggleReaction = async (emoji) => {
    const { data } = await api.post(`/community/posts/${post.post_id}/react`, { emoji });
    setReactions((prev) => { const n = { ...prev }; n[emoji] = (n[emoji] || 0) + (data.reacted ? 1 : -1); if (n[emoji] <= 0) delete n[emoji]; return n; });
    setMyReactions((prev) => (data.reacted ? [...prev, emoji] : prev.filter((e) => e !== emoji)));
  };
  const report = async () => {
    await api.post("/community/report", { target_type: "post", target_id: post.post_id, reason: "reported by member" });
    toast.success("Thank you. Our team will review this.");
  };
  const block = async () => {
    if (!window.confirm("Block this member? You won't see their posts or comments.")) return;
    await api.post("/community/block", { user_id: post.user_id });
    toast.success("Member blocked."); onChange();
  };
  const del = async () => {
    if (!window.confirm("Delete your post?")) return;
    await api.delete(`/community/posts/${post.post_id}`); onChange();
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      data-testid={COMMUNITY.postCard} className="bg-white border border-[#291B2D]/5 rounded-2xl p-5 card-soft">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-9 w-9 rounded-full bg-[#F3EEF4] text-[#7B4C74] flex items-center justify-center font-semibold">
          {post.anonymous ? "♡" : (post.author_name || "S").charAt(0).toUpperCase()}
        </div>
        <div>
          <p className="font-medium text-sm">{post.author_name}</p>
          <p className="text-xs text-[#8A838E]">{new Date(post.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</p>
        </div>
      </div>
      <p className="text-[#252326] leading-relaxed whitespace-pre-wrap">{post.body}</p>
      {post.image && <img src={post.image} alt="shared" className="mt-3 rounded-xl max-h-80 w-auto" />}
      <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="community-reactions">
        {REACTIONS.map((e) => {
          const mine = myReactions.includes(e); const n = reactions[e] || 0;
          return (
            <button key={e} data-testid={`community-react-${e}`} onClick={() => toggleReaction(e)}
              className={`text-sm rounded-full px-2.5 py-1 border transition-smooth ${mine ? "bg-[#F3EEF4] border-[#7B4C74]/40" : "bg-white border-[#291B2D]/10 hover:bg-[#F7F2EA]"}`}>
              <span>{e}</span>{n > 0 && <span className="ml-1 text-xs text-[#59535B]">{n}</span>}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-4 mt-4 text-sm text-[#59535B]">
        <button data-testid={COMMUNITY.likeButton} onClick={like} className="flex items-center gap-1.5 hover:text-[#B97978] transition-smooth">
          <Heart className={`h-4 w-4 ${liked ? "fill-[#B97978] text-[#B97978]" : ""}`} /> {likes}
        </button>
        <button data-testid={COMMUNITY.commentToggle} onClick={toggleComments} className="flex items-center gap-1.5 hover:text-[#291B2D] transition-smooth">
          <MessageCircle className="h-4 w-4" /> {post.comment_count}
        </button>
        <div className="ml-auto flex items-center gap-3">
          {post.is_mine ? (
            <button data-testid={COMMUNITY.deleteButton} onClick={del} className="hover:text-[#9E3A3A]" aria-label="Delete post"><Trash2 className="h-4 w-4" /></button>
          ) : (
            <>
              <button data-testid={COMMUNITY.reportButton} onClick={report} className="hover:text-[#9E3A3A]" aria-label="Report post"><Flag className="h-4 w-4" /></button>
              <button data-testid={COMMUNITY.blockButton} onClick={block} className="hover:text-[#9E3A3A]" aria-label="Block member"><Ban className="h-4 w-4" /></button>
            </>
          )}
        </div>
      </div>
      {showComments && (
        <div className="mt-4 pt-4 border-t border-[#E5DED4] space-y-3">
          {comments.map((c) => <CommentItem key={c.comment_id} comment={c} />)}
          {cimage && (
            <div className="relative inline-block">
              <img src={cimage} alt="reply preview" className="max-h-32 rounded-lg" />
              <button onClick={() => setCimage(null)} aria-label="Remove image"
                className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-[#291B2D] text-white flex items-center justify-center text-xs">×</button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <label data-testid="community-comment-image" className="h-9 w-9 shrink-0 rounded-full border border-[#E5DED4] flex items-center justify-center cursor-pointer text-[#59535B] hover:text-[#291B2D]">
              <ImageIcon className="h-4 w-4" />
              <input type="file" accept="image/*" onChange={pickCommentImage} className="hidden" />
            </label>
            <input data-testid={COMMUNITY.commentInput} value={cbody} onChange={(e) => setCbody(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addComment(); }}
              placeholder="Add a kind word…"
              className="flex-1 bg-[#FCFAF6] border border-[#E5DED4] rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C89B55]" />
            <MicButton testid="community-comment-dictate" className="relative h-9 w-9 shrink-0"
              onAppend={(t) => setCbody((b) => (b ? b + " " + t : t))} />
            <button data-testid={COMMUNITY.commentSubmit} onClick={addComment} aria-label="Send comment"
              className="h-9 w-9 rounded-full bg-[#291B2D] text-[#FCFAF6] flex items-center justify-center"><Send className="h-4 w-4" /></button>
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default function Community() {
  const [circles, setCircles] = useState([]);
  const [guidelines, setGuidelines] = useState([]);
  const [active, setActive] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    track("community_viewed");
    api.get("/community/circles").then(({ data }) => { setCircles(data.circles); setGuidelines(data.guidelines); });
  }, []);

  const openCircle = async (circle) => {
    setActive(circle);
    const { data } = await api.get(`/community/circles/${circle.id}/posts`);
    setData(data);
  };
  const reload = () => active && openCircle(active);

  if (active && data) {
    return (
      <div className="space-y-6 max-w-2xl mx-auto">
        <button data-testid={COMMUNITY.backButton} onClick={() => { setActive(null); setData(null); }}
          className="flex items-center gap-2 text-[#59535B] hover:text-[#252326]">
          <ArrowLeft className="h-4 w-4" /> All circles
        </button>
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">Circle</p>
          <h1 className="font-heading text-3xl font-semibold mt-1">{data.circle.title}</h1>
          <p className="text-[#59535B] mt-2">{data.circle.description}</p>
        </div>
        <Composer circle={data.circle} onPosted={reload} />
        {data.posts.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-10 text-center bg-white rounded-3xl border border-[#291B2D]/5">
            <span className="w-14 h-14 rounded-full bg-[#F3EEF4] text-[#7B4C74] flex items-center justify-center mb-3"><Icons.MessagesSquare className="h-6 w-6" /></span>
            <p className="font-heading text-lg">Be the first to share.</p>
            <p className="text-sm text-[#8A838E] mt-1 max-w-sm">Your honesty makes it safer for the next woman to open up too.</p>
          </div>
        ) : data.posts.map((p) => <PostCard key={p.post_id} post={p} onChange={reload} />)}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">Community</p>
        <h1 className="font-heading text-3xl lg:text-4xl font-semibold mt-1">Circles of sisters</h1>
        <p className="text-[#59535B] mt-2 max-w-xl">Safe, supportive spaces to be honest and cheer each other on — for women from every background, with room for the stories that make us different.</p>
      </div>

      <div className="bg-[#F3EEF4] border border-[#7B4C74]/15 rounded-2xl p-5">
        <p className="flex items-center gap-2 text-sm font-semibold text-[#7B4C74] mb-2"><ShieldCheck className="h-4 w-4" /> Community guidelines</p>
        <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
          {guidelines.map((g, i) => <li key={i} className="text-sm text-[#59535B] flex items-start gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#7B4C74] mt-2 shrink-0" />{g}</li>)}
        </ul>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {circles.map((c, i) => {
          const Icon = Icons[c.icon] || Icons.MessagesSquare;
          return (
            <motion.button key={c.id} data-testid={`${COMMUNITY.circleCard}-${c.id}`} onClick={() => openCircle(c)}
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.04 }}
              className="text-left bg-white border border-[#291B2D]/5 rounded-2xl p-6 card-soft hover:-translate-y-0.5 transition-smooth">
              <div className="h-11 w-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: c.color }}>
                <Icon className="h-5 w-5 text-[#291B2D]" strokeWidth={1.9} />
              </div>
              <h3 className="font-heading text-lg font-semibold">{c.title}</h3>
              <p className="text-[#59535B] text-sm mt-2 leading-relaxed">{c.description}</p>
              <p className="text-xs text-[#8A838E] mt-3 flex items-center gap-1.5"><Lock className="h-3 w-3" /> {c.post_count} {c.post_count === 1 ? "post" : "posts"}</p>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
